/**
 * Gift Card Recipient Form Web Component
 * Handles the gift card recipient form functionality including:
 * - Toggle form fields visibility based on checkbox state
 * - Email requirement when sending to recipient
 * - Timezone offset for scheduled delivery
 * - Form reset after successful cart add
 * - Accessibility announcements
 */

if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;

        // Query form elements
        this.checkboxInput = this.querySelector(`#Recipient-Checkbox-${this.sectionId}-${this.getProductId()}`);
        if (!this.checkboxInput) {
          // Fallback for different ID patterns
          this.checkboxInput = this.querySelector('input[type="checkbox"]');
        }

        this.hiddenControlField = this.querySelector(`#Recipient-Control-${this.sectionId}`);
        this.emailInput = this.querySelector(`#Recipient-email-${this.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.sectionId}`);
        this.sendOnInput = this.querySelector(`#Recipient-send_on-${this.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-Offset-${this.sectionId}`);
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.sectionId}`);
        this.recipientFields = this.querySelector('.recipient-fields');

        // Error handling elements
        this.errorMessageWrapper = this.querySelector('.recipient-form__error-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;

        // Store current variant ID for cart events
        this.currentProductVariantId = this.dataset.productVariantId;

        // Form wrapper for cart events
        this.form = this.closest('[data-form-wrapper]');

        // Initialize the form
        this.init();
      }

      getProductId() {
        // Extract product ID from checkbox ID if available
        const checkbox = this.querySelector('input[type="checkbox"]');
        if (checkbox && checkbox.id) {
          const match = checkbox.id.match(/Recipient-Checkbox-[^-]+-(\d+)/);
          return match ? match[1] : '';
        }
        return '';
      }

      init() {
        // Enable checkbox (disabled by default for no-JS fallback)
        if (this.checkboxInput) {
          this.checkboxInput.disabled = false;
        }

        // Disable hidden control field when JS is enabled
        if (this.hiddenControlField) {
          this.hiddenControlField.disabled = true;
        }

        // Set timezone offset
        if (this.offsetProperty) {
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        // Add event listeners
        this.addEventListener('change', this.onChange.bind(this));

        // Initialize form state
        this.onChange();
      }

      connectedCallback() {
        // Keyboard support for checkbox toggle with Enter key
        if (this.checkboxInput) {
          this.checkboxInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.checkboxInput.checked = !this.checkboxInput.checked;
              this.checkboxInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        // Listen for cart add events to reset form
        if (this.form) {
          this.form.addEventListener('theme:product:add', () => {
            this.resetRecipientForm();
          });
        }

        // Subscribe to pub/sub events if available (for themes using pub/sub pattern)
        this.subscribeToEvents();
      }

      subscribeToEvents() {
        // Check if pub/sub system is available
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
          // Cart update subscription
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source === 'product-form' && event.productVariantId?.toString() === this.currentProductVariantId) {
              this.resetRecipientForm();
            }
          });

          // Variant change subscription
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId === this.sectionId) {
              this.currentProductVariantId = event.data.variant.id.toString();
            }
          });

          // Cart error subscription
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
            if (event.source === 'product-form' && event.productVariantId?.toString() === this.currentProductVariantId) {
              this.displayErrorMessage(event.message, event.errors);
            }
          });
        }
      }

      disconnectedCallback() {
        // Unsubscribe from pub/sub events
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange(event) {
        const isChecked = this.checkboxInput?.checked;

        if (isChecked) {
          this.enableInputFields();
          this.announceToScreenReader('expanded');
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.announceToScreenReader('collapsed');
        }
      }

      inputFields() {
        return [
          this.emailInput,
          this.nameInput,
          this.messageInput,
          this.sendOnInput
        ].filter(Boolean);
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty].filter(Boolean);
      }

      clearInputFields() {
        this.inputFields().forEach((field) => {
          if (field) {
            field.value = '';
          }
        });
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => {
          if (field) {
            field.disabled = false;
          }
        });

        // Make email required when form is enabled
        if (this.emailInput) {
          this.emailInput.required = true;
        }
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => {
          if (field) {
            field.disabled = true;
          }
        });

        // Remove required from email when form is disabled
        if (this.emailInput) {
          this.emailInput.required = false;
        }
      }

      announceToScreenReader(state) {
        if (this.recipientFieldsLiveRegion && window.accessibilityStrings) {
          const message = state === 'expanded'
            ? window.accessibilityStrings.recipientFormExpanded
            : window.accessibilityStrings.recipientFormCollapsed;

          if (message) {
            this.recipientFieldsLiveRegion.innerText = message;
          }
        }
      }

      displayErrorMessage(title, body) {
        if (!this.errorMessageWrapper) return;

        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;

        if (typeof body === 'object') {
          if (this.errorMessage) {
            this.errorMessage.innerText = this.defaultErrorHeader;
          }

          Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.sectionId}`;
            const message = Array.isArray(value) ? value.join(', ') : value;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');

            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            // Set aria-invalid on the input
            const inputElement = this.querySelector(fieldSelector);
            if (inputElement) {
              inputElement.setAttribute('aria-invalid', 'true');
              inputElement.setAttribute('aria-describedby', errorMessageId);
            }
          });
        } else if (this.errorMessage) {
          this.errorMessage.innerText = body;
        }
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.hidden = true;
        }

        if (this.errorMessageList) {
          this.errorMessageList.innerHTML = '';
        }

        // Clear individual field errors
        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) {
            textField.innerText = '';
          }
        });

        // Reset aria attributes on inputs
        this.inputFields().forEach((inputElement) => {
          if (inputElement) {
            inputElement.setAttribute('aria-invalid', 'false');
            inputElement.removeAttribute('aria-describedby');
          }
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput?.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
          this.checkboxInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  );
}
